import React from 'react';
import type { IntlShape } from 'react-intl';

import { SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import type { TableRow } from '@/components/atoms/Table/Table.type';
import { computeText } from '@/locales/utils';
import { theme } from '@/core/theme';
import type { BrandPromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';

import { buildBannerActionCell } from '@/pages/PromotionalBanners/utils/buildBannerActionCell';
import {
    buildBannerStatusBadge,
    buildBannerLinkTypeCell,
} from '@/pages/PromotionalBanners/utils/buildBannerStatusBadge';

type BuildRowsParams = {
    banners: BrandPromotionalBanner[];
    intl: IntlShape;
    onToggle: (banner: BrandPromotionalBanner) => void;
    onEdit: (banner: BrandPromotionalBanner) => void;
    onDelete: (banner: BrandPromotionalBanner) => void;
};

function getLinkLabel(banner: BrandPromotionalBanner, intl: IntlShape): { badgeText: string; subtitle?: string } {
    if (banner.linkType === 'none') {
        return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
    }
    if (banner.linkType === 'product') {
        if (!banner.linkTargetRef) {
            return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
        }
        return {
            badgeText: computeText(intl, 'promotionalBanner.table.linkProduct'),
            subtitle: banner.linkTargetRef,
        };
    }
    if (banner.linkType === 'category') {
        if (!banner.linkTargetRef) {
            return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
        }
        return {
            badgeText: computeText(intl, 'promotionalBanner.table.linkCategory'),
            subtitle: banner.linkTargetRef,
        };
    }
    return { badgeText: '' };
}

export const buildBrandPromotionalBannerRows = ({
    banners,
    intl,
    onToggle,
    onEdit,
    onDelete,
}: BuildRowsParams): TableRow[] =>
    banners.map((banner): TableRow => {
        const linkInfo = getLinkLabel(banner, intl);
        const isToggleDisabled = !banner.isActive && banner.targetCount === 0;

        return {
            id: { type: 'identifier' as const, value: banner.promotionalBannerId },
            name: {
                type: 'string' as const,
                value: {
                    text: (
                        <>
                            {banner.name}
                            <span
                                style={{
                                    display: 'block',
                                    marginTop: theme.savory.spacing[1],
                                    color: theme.savory.colors.neutral[500],
                                    fontSize: '12px',
                                }}
                            >
                                {new Date(banner.created_at).toLocaleDateString('fr-FR')}
                            </span>
                        </>
                    ),
                    noTranslation: true,
                    value: banner.name,
                },
            },
            status: {
                type: 'badgeSubtitle' as const,
                value: {
                    badge: buildBannerStatusBadge(banner.isActive, intl),
                    subtitle: computeText(intl, 'brandPromotionalBanner.table.activeRestaurants', {
                        count: banner.activationCount,
                    }),
                },
            },
            source: {
                type: 'badge' as const,
                value: {
                    icon: SVGIcons.BRAND_V2,
                    text: computeText(intl, 'promotionalBanner.table.sourceBrand'),
                    textColor: theme.savory.colors.neutral[900],
                    backgroundColor: theme.savory.colors.neutral[100],
                },
            },
            linkType: buildBannerLinkTypeCell(linkInfo),
            actions: buildBannerActionCell({
                isActive: banner.isActive,
                intl,
                onToggle: () => onToggle(banner),
                isToggleDisabled,
                dropdownItems: [
                    {
                        value: 1,
                        label: computeText(intl, 'promotionalBanner.action.edit'),
                        onClick: () => onEdit(banner),
                    },
                    {
                        value: 2,
                        label: computeText(intl, 'promotionalBanner.action.delete'),
                        onClick: () => onDelete(banner),
                    },
                ],
            }),
        };
    });
